import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AutoInvoiceSchedule, fmtSchedule } from "@/components/billing/AutoInvoiceSchedule";

const CATS = [
  ["eligible", "Akan dibuatkan invoice", "text-[#047857]"],
  ["already_invoiced", "Sudah punya invoice periode ini", "text-[#374151]"],
  ["review_started_in_period", "Perlu tinjauan: mulai di periode ini tapi sudah punya invoice", "text-[#B45309]"],
  ["not_yet_started", "Belum memenuhi syarat (mulai di bulan periode atau sesudahnya)", "text-[#374151]"],
  ["invalid_start_date", "Tanggal mulai kosong/tidak valid — periksa", "text-[#B91C1C]"],
  ["package_missing", "Paket tidak ditemukan — periksa", "text-[#B91C1C]"],
];

const CatRow = ({ k, label, cls, rows }) => (
  <details className="border rounded-md" data-testid={`autoinv-cat-${k}`}>
    <summary className="flex justify-between gap-2 px-2 py-1.5 cursor-pointer text-xs">
      <span className={cls}>{label}</span><b className="font-mono" data-testid={`autoinv-count-${k}`}>{rows.length}</b>
    </summary>
    {rows.length > 0 && (
      <ul className="max-h-32 overflow-y-auto text-[11px] px-2 pb-1.5 space-y-0.5">
        {rows.map((r) => (
          <li key={r.customer_id} className="flex justify-between gap-2">
            <span className="truncate">{r.nama_customer} <span className="font-mono text-[#6B7280]">{r.customer_id}</span></span>
            <span className="font-mono">{r.tanggal_mulai || "—"}</span>
          </li>
        ))}
      </ul>
    )}
  </details>
);

const RunsTable = ({ runs }) => (
  <div className="max-h-40 overflow-y-auto border rounded-md" data-testid="autoinv-runs">
    <table className="w-full text-[11px]">
      <thead className="bg-[#F8FAFC] text-[#6B7280]"><tr><th className="px-2 py-1 text-left">Periode</th><th className="px-2 py-1 text-left">Pemicu</th><th className="px-2 py-1 text-left">Status</th><th className="px-2 py-1 text-right">Dibuat</th><th className="px-2 py-1 text-right">Gagal</th><th className="px-2 py-1 text-left">Mulai</th></tr></thead>
      <tbody>
        {runs.length === 0 && <tr><td colSpan={6} className="px-2 py-2 text-center text-[#6B7280]">Belum ada run.</td></tr>}
        {runs.map((r) => (
          <tr key={r.run_id} className="border-t">
            <td className="px-2 py-1 font-mono">{r.periode}</td><td className="px-2 py-1">{r.trigger}</td><td className="px-2 py-1">{r.status}</td>
            <td className="px-2 py-1 text-right font-mono">{r.created ?? "—"}</td><td className="px-2 py-1 text-right font-mono">{r.failed_count ?? 0}</td>
            <td className="px-2 py-1 font-mono">{String(r.started_at || "").slice(0, 16).replace("T", " ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const AutoInvoiceDialog = ({ onClose, onDone, isAdmin = false }) => {
  // Okt 2026 — Admin: pratinjau + catch-up Invoice Otomatis (aturan sama dgn scheduler).
  const [periode, setPeriode] = useState(() => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 7));
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState({ items: [] });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sched, setSched] = useState(null);

  const loadRuns = () => api.get("/billing/auto-invoice/runs").then(({ data }) => setRuns(data)).catch(() => {});
  useEffect(() => { loadRuns(); }, []);

  const doPreview = async () => {
    setBusy(true); setResult(null); setConfirm(false);
    try { setPreview((await api.get("/billing/auto-invoice/preview", { params: { periode_tagihan: periode } })).data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat pratinjau"); }
    finally { setBusy(false); }
  };
  const doRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/auto-invoice/run", {
        periode_tagihan: preview.periode, confirm: true, expected_eligible: preview.counts.eligible,
      });
      setResult(data); setPreview(null); toast.success(`${data.created} invoice dibuat.`);
      loadRuns(); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal menjalankan"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="autoinv-dialog">
        <DialogHeader>
          <DialogTitle>Invoice Otomatis</DialogTitle>
          <DialogDescription className="text-xs">
            Terjadwal {sched ? fmtSchedule(sched) : "bulanan (WIB)"}. Hanya customer AKTIF yang mulai sebelum bulan periode.
            Generate Invoice manual tidak terpengaruh.
            {sched?.effective_enabled !== false && runs.current_periode && !runs.current_periode_processed && runs.current_periode_due_passed && (
              <span className="block text-[#B45309] mt-1" data-testid="autoinv-missed">Periode {runs.current_periode} belum diproses otomatis — gunakan pratinjau & jalankan susulan bila diperlukan.</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <AutoInvoiceSchedule isAdmin={isAdmin} onLoaded={setSched} />
        <div className="flex items-end gap-2">
          <div><Label htmlFor="autoinv-periode">Periode</Label>
            <Input id="autoinv-periode" type="month" value={periode} onChange={(e) => { setPeriode(e.target.value); setPreview(null); }} className="w-44" data-testid="autoinv-periode" /></div>
          <Button variant="outline" onClick={doPreview} disabled={busy || !periode} data-testid="autoinv-preview-btn">{busy && !preview ? "Memuat…" : "Pratinjau"}</Button>
        </div>
        {preview && (
          <div className="space-y-1.5" data-testid="autoinv-preview">
            <div className="text-xs text-[#6B7280]">Diperiksa {preview.checked} customer AKTIF · non-aktif dilewati: {Object.entries(preview.status_non_aktif).map(([k, v]) => `${k} ${v}`).join(", ") || "0"}</div>
            {CATS.map(([k, label, cls]) => <CatRow key={k} k={k} label={label} cls={cls} rows={preview[k] || []} />)}
            <label className="flex items-start gap-2 text-xs pt-1">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="autoinv-confirm" />
              <span>Saya sudah meninjau dan ingin membuat <b>{preview.counts.eligible}</b> invoice periode {preview.periode}. Invoice lama, pembayaran, dan tunggakan tidak diubah.</span>
            </label>
          </div>
        )}
        {result && (
          <div className="text-xs rounded-md border bg-[#ECFDF5] p-2" data-testid="autoinv-result">
            Run {result.status}: <b>{result.created}</b> dibuat, {result.skipped_duplicate} duplikat dilewati, {result.failed_count} gagal.
            {result.failed?.length > 0 && <ul className="mt-1">{result.failed.map((f) => <li key={f.customer_id} className="font-mono">{f.customer_id} — {f.reason}</li>)}</ul>}
          </div>
        )}
        <div className="text-xs font-bold text-[#4B5563] pt-1">Riwayat run</div>
        <RunsTable runs={runs.items || []} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="autoinv-close">Tutup</Button>
          {preview && (
            <Button onClick={doRun} disabled={busy || !confirm || preview.counts.eligible === 0} className="bg-[#0D9488] hover:bg-[#0F766E] text-white" data-testid="autoinv-run-btn">
              {busy ? "Menjalankan…" : `Jalankan (${preview.counts.eligible})`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
